import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  EmptyState,
  FilterToolbar,
  ReportTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import {
  exportPatientRosterReportReportsPatientRosterExportGet,
  getPatientRosterClinicOptionsReportsPatientRosterClinicOptionsGet,
  getPatientRosterReportReportsPatientRosterGet,
} from '../services/api/sdk.gen';
import type {
  PatientRosterClinicOption,
  PatientRosterReportResult,
  PatientRosterReportRow,
} from '../services/api/types.gen';
import { PATIENTROSTERSORTBY_VALUES } from '../services/api/models/PatientRosterSortBy';
import { SORTORDER_VALUES } from '../services/api/models/SortOrder';
import { PermissionSlug } from '../constants/featureFlags';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { ReportPopulationControls } from '../components/reporting/ReportPopulationControls';
import { buildReportPopulationQuery } from '../components/reporting/reportPopulation';
import {
  getStoredIncludeImportedPatients,
  setStoredIncludeImportedPatients,
} from '../utils/populationPreferences';
import { useDebounce } from '../hooks/useDebounce';
import { downloadResponseAsFile } from '../utils/downloadFile';
import { formatDateToISO, parseISOToLocalDate } from '../utils/dateUtils';
import {
  coerceReportPageSize,
  formatReportDate,
  formatReportEmptyValue,
  readBooleanParam,
  readOptionalChoiceParam,
  readPositiveIntegerParam,
  readReportDateParam,
  REPORT_PAGE_SIZE_OPTIONS,
} from '../utils/reportDisplay';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import { ReportLink } from '../components/reporting/ReportLink';
import { patientChartPath } from '../utils/navigation';
import ReportsPermissionAlert from './ReportsPermissionAlert';
import { useReportsBackHref } from '../reporting/backHref';

const ALL = '__all__';

const columns: ReportTableColumn<PatientRosterReportRow>[] = [
  {
    id: 'clinic',
    header: 'Clinic',
    accessor: row => formatReportEmptyValue(row.clinic),
    enableSorting: true,
    minWidth: 150,
  },
  {
    id: 'patient_first',
    header: 'Patient First',
    accessor: row =>
      `${row.patient_first}${row.patient_gender === 'F' ? ' (F)' : ''}`,
    render: (_value, row) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {`${row.patient_first}${row.patient_gender === 'F' ? ' (F)' : ''}`}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'patient_last',
    header: 'Patient Last',
    accessor: 'patient_last',
    render: (_value, row) => (
      <ReportLink to={patientChartPath(row.patient_id)}>
        {row.patient_last}
      </ReportLink>
    ),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'mrn',
    header: 'MRN',
    accessor: row => formatReportEmptyValue(row.mrn),
    enableSorting: true,
    minWidth: 110,
  },
  {
    id: 'phone',
    header: 'Phone',
    accessor: row => formatReportEmptyValue(row.phone),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'date_of_birth',
    header: 'DOB',
    accessor: row => formatReportDate(row.date_of_birth),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'age',
    header: 'Age',
    accessor: row => formatReportEmptyValue(row.age),
    enableSorting: true,
    minWidth: 80,
  },
  {
    id: 'address',
    header: 'Address',
    accessor: row => formatReportEmptyValue(row.address),
    enableSorting: true,
    minWidth: 220,
  },
  {
    id: 'triage_code',
    header: 'Triage / Risk',
    accessor: row => formatReportEmptyValue(row.triage_code),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'disaster_code',
    header: 'Disaster',
    accessor: row => formatReportEmptyValue(row.disaster_code),
    enableSorting: true,
    minWidth: 110,
  },
  {
    id: 'pdx',
    header: 'PDX',
    accessor: row => formatReportEmptyValue(row.pdx),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'sdx',
    header: 'SDX',
    accessor: row => formatReportEmptyValue(row.sdx),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'episode_start_date',
    header: 'Episode Start',
    accessor: row => formatReportDate(row.episode_start_date),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'episode_end_date',
    header: 'Episode End',
    accessor: row => formatReportDate(row.episode_end_date),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'oasis_date',
    header: 'OASIS Date',
    accessor: row => formatReportDate(row.oasis_date),
    enableSorting: true,
    minWidth: 120,
  },
  {
    id: 'payer',
    header: 'Payer',
    accessor: row => formatReportEmptyValue(row.payer),
    enableSorting: true,
    minWidth: 160,
  },
  {
    id: 'soc_date',
    header: 'Admission SOC',
    accessor: row => formatReportDate(row.soc_date),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'physician_name',
    header: 'Physician',
    accessor: row => formatReportEmptyValue(row.physician_name),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'physician_npi',
    header: 'Physician NPI',
    accessor: row => formatReportEmptyValue(row.physician_npi),
    enableSorting: true,
    minWidth: 130,
  },
  {
    id: 'physician_phone',
    header: 'Physician Phone',
    accessor: row => formatReportEmptyValue(row.physician_phone),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'physician_fax',
    header: 'Physician Fax',
    accessor: row => formatReportEmptyValue(row.physician_fax),
    enableSorting: true,
    minWidth: 140,
  },
  {
    id: 'assigned_clinician',
    header: 'Assigned Clinician',
    accessor: row => formatReportEmptyValue(row.assigned_clinician),
    enableSorting: true,
    minWidth: 180,
  },
  {
    id: 'clinician_credentials',
    header: 'Clinician Credentials',
    accessor: row => formatReportEmptyValue(row.clinician_credentials),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'case_manager',
    header: 'Case Manager',
    accessor: row => formatReportEmptyValue(row.case_manager),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'clinical_manager',
    header: 'Clinical Manager',
    accessor: row => formatReportEmptyValue(row.clinical_manager),
    enableSorting: true,
    minWidth: 170,
  },
  {
    id: 'scheduled_disciplines',
    header: 'Scheduled Disciplines',
    accessor: row => formatReportEmptyValue(row.scheduled_disciplines),
    enableSorting: true,
    minWidth: 190,
  },
];

const PatientRosterReport = () => {
  const { hasPermission } = useStore(
    useShallow(state => ({ hasPermission: state.hasPermission }))
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<PatientRosterReportResult | null>(null);
  const [clinics, setClinics] = useState<PatientRosterClinicOption[]>([]);
  const [searchText, setSearchText] = useState(searchParams.get('q') ?? '');
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reportsBackHref = useReportsBackHref();

  const startDate = readReportDateParam(searchParams.get('start_date'));
  const endDate = readReportDateParam(searchParams.get('end_date'));
  const clinicId = searchParams.get('clinic') ?? '';
  const q = searchParams.get('q') ?? '';
  const includeTestRows =
    readBooleanParam(searchParams.get('include_test_rows')) ?? false;
  const includeInactiveEpisodes =
    readBooleanParam(searchParams.get('include_inactive_episodes')) ?? false;
  const includeImportedPatientsParam = searchParams.get(
    'include_imported_patients'
  );
  const includeImportedPatients =
    includeImportedPatientsParam !== null
      ? includeImportedPatientsParam === 'true'
      : getStoredIncludeImportedPatients();
  const page = readPositiveIntegerParam(searchParams.get('page'));
  const pageSize = coerceReportPageSize(Number(searchParams.get('size')));
  const sortBy = readOptionalChoiceParam(
    searchParams.get('sort'),
    PATIENTROSTERSORTBY_VALUES
  );
  const sortOrder = sortBy
    ? readOptionalChoiceParam(searchParams.get('dir'), SORTORDER_VALUES)
    : undefined;

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        current => {
          const next = new URLSearchParams(current);
          Object.entries(updates).forEach(([key, value]) =>
            value ? next.set(key, value) : next.delete(key)
          );
          if (!('page' in updates)) next.set('page', '1');
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const debouncedUpdateSearch = useDebounce(
    (value: string) => updateParams({ q: value || null }),
    300
  );

  useEffect(() => setSearchText(q), [q]);
  useEffect(() => {
    if (searchText !== q) debouncedUpdateSearch(searchText);
  }, [debouncedUpdateSearch, q, searchText]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    getPatientRosterClinicOptionsReportsPatientRosterClinicOptionsGet({})
      .then(response => {
        if (!cancelled && response.data) {
          setClinics(response.data.items);
          if (!startDate && !endDate) {
            updateParams({
              start_date: response.data.default_start_date,
              end_date: response.data.default_end_date,
            });
          }
        }
      })
      .catch(error =>
        console.error('Failed to load patient roster options', error)
      );
    return () => {
      cancelled = true;
    };
  }, [endDate, hasPermission, startDate, updateParams]);

  const query = useMemo(
    () => ({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      clinic_id: clinicId || undefined,
      q: q || undefined,
      ...buildReportPopulationQuery({
        includeTestRows,
        includeInactiveEpisodes,
        includeImportedPatients,
      }),
      sort_by: sortBy,
      sort_order: sortOrder,
      page,
      page_size: pageSize,
    }),
    [
      clinicId,
      endDate,
      includeInactiveEpisodes,
      includeTestRows,
      includeImportedPatients,
      page,
      pageSize,
      q,
      sortBy,
      sortOrder,
      startDate,
    ]
  );

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    getPatientRosterReportReportsPatientRosterGet({ query })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          setReport(null);
          setError(
            getBusinessErrorMessage(response) ??
              'Unable to load patient roster. Please try again.'
          );
        } else setReport(response.data);
      })
      .catch(error => {
        console.error('Failed to load patient roster report', error);
        if (!cancelled) {
          setReport(null);
          setError(
            getBusinessErrorMessage(error) ??
              'Unable to load patient roster. Please try again.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasPermission, query]);

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS))
    return <ReportsPermissionAlert />;

  const download = async () => {
    setIsExporting(true);
    try {
      const { page: _page, page_size: _pageSize, ...exportQuery } = query;
      void _page;
      void _pageSize;
      const response =
        await exportPatientRosterReportReportsPatientRosterExportGet({
          query: exportQuery,
          responseType: 'blob',
        });
      if (response.error || !response.data) {
        setError(
          await getUserFacingApiMessageAsync(
            response,
            'Unable to export patient roster. Please try again.'
          )
        );
      } else {
        downloadResponseAsFile(
          response,
          `RogerPatientRoster_${report?.start_date ?? startDate}_${report?.end_date ?? endDate}.xlsx`
        );
      }
    } catch (error) {
      console.error('Failed to export patient roster', error);
      setError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to export patient roster. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  const sorting: ReportTableSort | null = sortBy
    ? { id: sortBy, direction: sortOrder ?? 'asc' }
    : null;
  const pagination: ReportTablePaginationState = { page, pageSize };
  const selectedStart = report?.start_date ?? startDate;
  const selectedEnd = report?.end_date ?? endDate;

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center gap-1 text-sm text-muted-foreground'
      >
        <ArrowLeft className='h-4 w-4' /> Reports
      </Link>
      <div className='flex flex-col gap-3 md:flex-row md:items-start'>
        <div className='flex flex-wrap items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => void download()}
            disabled={isLoading || isExporting}
          >
            <Download className='h-4 w-4' />{' '}
            {isExporting ? 'Exporting…' : 'Export'}
          </Button>
          <ReportPopulationControls
            includeTestRows={includeTestRows}
            includeInactiveEpisodes={includeInactiveEpisodes}
            includeImportedPatients={includeImportedPatients}
            onIncludeTestRowsChange={checked =>
              updateParams({ include_test_rows: checked ? 'true' : null })
            }
            onIncludeInactiveEpisodesChange={checked =>
              updateParams({
                include_inactive_episodes: checked ? 'true' : null,
              })
            }
            onIncludeImportedPatientsChange={checked => {
              setStoredIncludeImportedPatients(checked);
              updateParams({ include_imported_patients: String(checked) });
            }}
          />
        </div>
      </div>
      {error ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>{error}</AlertDescription>
        </Alert>
      ) : null}
      <FilterToolbar
        search={{
          value: searchText,
          onChange: setSearchText,
          label: 'Search',
          placeholder: 'Search patient, MRN, or code...',
          'aria-label': 'Search patient roster',
        }}
        reset={{
          label: 'Reset',
          onClick: () =>
            setSearchParams(new URLSearchParams(), { replace: true }),
        }}
      >
        <label className='flex flex-col gap-1 text-sm'>
          Start
          <DatePicker
            date={parseISOToLocalDate(selectedStart)}
            datePickerSize='default'
            onDateChange={date =>
              updateParams({ start_date: date ? formatDateToISO(date) : null })
            }
            showClearButton
          />
        </label>
        <label className='flex flex-col gap-1 text-sm'>
          End
          <DatePicker
            date={parseISOToLocalDate(selectedEnd)}
            datePickerSize='default'
            onDateChange={date =>
              updateParams({ end_date: date ? formatDateToISO(date) : null })
            }
            showClearButton
          />
        </label>
        <label className='flex flex-col gap-1 text-sm'>
          Clinic
          <Select
            value={clinicId || ALL}
            onValueChange={value =>
              updateParams({ clinic: value === ALL ? null : value })
            }
          >
            <SelectTrigger selectSize='default'>
              <SelectValue placeholder='All clinics' />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>All clinics</SelectItem>
              {clinics.map(clinic => (
                <SelectItem key={clinic.id} value={clinic.id}>
                  {clinic.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </FilterToolbar>
      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.row_id}
        caption='Patient roster report results'
        sorting={sorting}
        onSortingChange={next =>
          updateParams(
            next
              ? { sort: next.id, dir: next.direction }
              : { sort: null, dir: null }
          )
        }
        isLoading={isLoading}
        emptyState={
          <EmptyState
            title={error ?? 'No patients match these filters'}
            description='Adjust the filters and try again.'
          />
        }
        className='min-w-[1400px] text-xs'
        pagination={{
          ...pagination,
          total: report?.total ?? 0,
          totalPages: Math.ceil((report?.total ?? 0) / pageSize),
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
        }}
        onPaginationChange={next =>
          setSearchParams(current => {
            const params = new URLSearchParams(current);
            params.set('page', String(next.page));
            params.set('size', String(next.pageSize));
            return params;
          })
        }
      />
    </div>
  );
};

export default PatientRosterReport;
